import { useEffect, useRef, forwardRef, useImperativeHandle, useState } from 'react';
import { Loader2 } from 'lucide-react';

// Drag-and-drop email template editor (Templatical) embedded in React.
// The editor bundle is ~10 MB, so everything is loaded via dynamic import the
// first time the component mounts — it never touches the main bundle.
//
// Content pipeline: the editor produces a JSON TemplateContent (stored for
// re-editing); compileHtml() renders JSON → MJML (@templatical/renderer) →
// responsive email HTML (mjml-browser), all client-side. Merge tags like
// {{guest_name}} survive into the HTML and are substituted server-side at send.

export interface EmailEditorHandle {
  getContent: () => unknown | null;
  compileHtml: () => Promise<string>;
}

export interface MergeTagDef { label: string; value: string; group?: string }

interface Props {
  initialContent?: unknown | null;
  mergeTags: MergeTagDef[];
  onDirty?: () => void;
  /** Fixed pixel height for the editor (it scrolls internally). Defaults to
   *  nearly the full viewport so there's room to design a whole email. */
  height?: number;
}

const EmailTemplateEditor = forwardRef<EmailEditorHandle, Props>(function EmailTemplateEditor(
  { initialContent, mergeTags, onDirty, height }, ref,
) {
  const editorHeight = height ?? (typeof window !== 'undefined' ? Math.max(620, window.innerHeight - 210) : 760);
  const containerRef = useRef<HTMLDivElement>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const editorRef = useRef<any>(null);
  const contentRef = useRef<unknown | null>(initialContent ?? null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { init } = await import('@templatical/editor');
        if (cancelled || !containerRef.current) return;
        const editor = await init({
          container: containerRef.current,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          ...(initialContent ? { content: initialContent as any } : {}),
          mergeTags: { syntax: 'handlebars', tags: mergeTags },
          branding: false,
          onChange: (content: unknown) => { contentRef.current = content; onDirty?.(); },
          onError: (e: Error) => console.error('templatical:', e),
        });
        if (cancelled) { editor?.unmount?.(); return; }
        editorRef.current = editor;
        if (!initialContent) contentRef.current = editor.getContent();
        setLoading(false);
      } catch (e) {
        console.error('editor load failed:', e);
        if (!cancelled) { setError(e instanceof Error ? e.message : 'load_failed'); setLoading(false); }
      }
    })();
    return () => {
      cancelled = true;
      try { editorRef.current?.unmount?.(); } catch { /* */ }
      editorRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useImperativeHandle(ref, () => ({
    getContent: () => {
      try { return editorRef.current ? editorRef.current.getContent() : contentRef.current; }
      catch { return contentRef.current; }
    },
    compileHtml: async () => {
      const content = editorRef.current ? editorRef.current.getContent() : contentRef.current;
      if (!content) throw new Error('empty');
      const [{ renderToMjml }, mjmlMod] = await Promise.all([
        import('@templatical/renderer'),
        import('mjml-browser'),
      ]);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const mjml = await renderToMjml(content as any);
      const res = mjmlMod.default(mjml, { validationLevel: 'soft' });
      if (!res.html) throw new Error('mjml_failed');
      return res.html;
    },
  }), []);

  return (
    <div className="relative rounded-xl border border-slate-200 overflow-hidden bg-white" style={{ height: editorHeight }}>
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-white z-10">
          <Loader2 className="animate-spin text-teal-500" size={28} />
        </div>
      )}
      {error && (
        <div className="absolute inset-0 flex items-center justify-center bg-white z-10 p-6 text-center text-sm text-rose-600">
          No se ha podido cargar el editor: {error}
        </div>
      )}
      <div ref={containerRef} style={{ height: '100%' }} />
    </div>
  );
});

export default EmailTemplateEditor;

// The merge tags offered across guest-message templates and newsletters.
export const GUEST_MERGE_TAGS: MergeTagDef[] = [
  { label: 'Nombre del huésped', value: 'guest_name' },
  { label: 'Alojamiento', value: 'property_name' },
  { label: 'Tu negocio', value: 'business_name' },
  { label: 'Código de reserva', value: 'booking_code' },
  { label: 'Fecha de entrada', value: 'check_in' },
  { label: 'Fecha de salida', value: 'check_out' },
  { label: 'Hora de entrada', value: 'check_in_time' },
  { label: 'Hora de salida', value: 'check_out_time' },
  { label: 'Noches', value: 'nights' },
  { label: 'Huéspedes', value: 'guests' },
  { label: 'Total', value: 'total' },
  { label: 'Pagado', value: 'amount_paid' },
  { label: 'Pendiente', value: 'balance_due' },
  { label: 'Enlace app del huésped', value: 'guest_app_url' },
  { label: 'Enlace de pago', value: 'pay_url' },
];

export const NEWSLETTER_MERGE_TAGS: MergeTagDef[] = [
  { label: 'Nombre del huésped', value: 'guest_name' },
  { label: 'Tu negocio', value: 'business_name' },
];
